/**
 * Genre Jukebox scroll animations and the "Join Waitlist" jump. Styles: assets/gj-animations.css.
 *
 * - Elements with `data-gj-animate` get `.gj-in` once, when 20% of them is visible, which starts
 *   their keyframes. `.gj-done` follows when the animation ends, to drop `will-change`.
 * - `[data-gj-stack]` (How it works cards): on desktop the whole row is revealed at once (the side
 *   cards slide out from behind the middle one); below 990px each card is revealed on its own.
 * - Links to `#waitlist` (header, hero, box promo) scroll smoothly to the footer waitlist form and
 *   focus its email field. In the open mobile menu, the menu closes first.
 *
 * The head snippet (snippets/gj-animations-head.liquid) only adds `gj-anim` to <html> when motion
 * is allowed; without it nothing is hidden and only the waitlist jump runs (without smoothness
 * when the visitor prefers reduced motion).
 */

const root = document.documentElement;
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const desktop = window.matchMedia('(min-width: 990px)');

/** @type {Window & { gjAnimationsReady?: boolean }} */ (window).gjAnimationsReady = true;

/** @param {Element} element */
function reveal(element) {
  if (element.classList.contains('gj-in')) return;

  element.classList.add('gj-in');
  /** @param {Event} event */
  const done = (event) => {
    if (event.target !== element) return;
    element.classList.add('gj-done');
    element.removeEventListener('animationend', done);
    element.removeEventListener('transitionend', done);
  };
  element.addEventListener('animationend', done);
  element.addEventListener('transitionend', done);
}

/** Shows everything at once (motion turned off, or no observer) */
function revealAll() {
  for (const element of document.querySelectorAll('[data-gj-animate], [data-gj-stack], [data-gj-stack] > *')) {
    element.classList.add('gj-in', 'gj-done');
  }
}

/** @param {Element} stack */
function revealStackCards(stack) {
  for (const card of stack.children) reveal(card);
}

function initAnimations() {
  if (!root.classList.contains('gj-anim')) return;

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;

        const element = entry.target;
        observer.unobserve(element);

        if (element.hasAttribute('data-gj-stack')) {
          reveal(element);
          // On desktop the row moves as one; on smaller screens each card waits for its own turn
          if (desktop.matches) revealStackCards(element);
        } else {
          reveal(element);
        }
      }
    },
    { threshold: 0.2 }
  );

  for (const element of document.querySelectorAll('[data-gj-animate], [data-gj-stack]')) {
    observer.observe(element);
  }
  for (const card of document.querySelectorAll('[data-gj-stack] > *')) {
    observer.observe(card);
  }

  // Switching layouts (rotating a tablet, resizing) never leaves cards stacked or hidden
  desktop.addEventListener('change', () => {
    for (const stack of document.querySelectorAll('[data-gj-stack]')) {
      if (desktop.matches) {
        if (stack.classList.contains('gj-in')) revealStackCards(stack);
      } else if (stack.classList.contains('gj-in')) {
        for (const card of stack.children) card.classList.add('gj-in', 'gj-done');
      }
    }
  });

  reducedMotion.addEventListener('change', () => {
    if (reducedMotion.matches) revealAll();
  });
}

initAnimations();

/* Join Waitlist: scroll to the footer form and focus the email field */
document.addEventListener('click', (event) => {
  if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey) return;

  const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
  if (!(link instanceof HTMLAnchorElement) || link.hash !== '#waitlist') return;
  if (link.pathname !== window.location.pathname || link.host !== window.location.host) return;

  const target = document.getElementById('waitlist');
  if (!target) return;

  event.preventDefault();

  const form = target.querySelector('form') ?? target;
  const input = target.querySelector('input[type="email"]');

  // A link inside the open mobile menu: close the menu first so the page can scroll again
  const dialog = link.closest('dialog[open]');
  if (dialog instanceof HTMLDialogElement) {
    dialog.close();
    root.style.overflow = '';
  }

  form.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'center' });
  if (input instanceof HTMLInputElement) input.focus({ preventScroll: true });
  history.replaceState(null, '', '#waitlist');
});
